// ABOUTME: Scope navigator component for selecting country default or specific scope overrides.
// ABOUTME: Renders a DashboardCard with scope list, search/filter, and add-scope modal.
import {
  Button,
  ControlledInput,
  DashboardCard,
  Icon,
  H3,
  TextSmallRegular,
  TextSemiBold,
} from '@hm/fundamental'
import {mdiCheck, mdiChevronLeft, mdiEarth, mdiInformationOutline, mdiMagnify} from '@mdi/js'
import React, {useMemo, useState} from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import emptyBackground from '../../assets/emptyBackground.svg'
import {SCOPE_LEVEL_FILTER_ALL, ScopeItem, ScopeLevel, ScopeLevelFilterValue, ScopeResultItem, VerifyScopeResult} from '../types'
import {SCOPE_LEVEL_OPTIONS} from '../utils'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {FilterDropdown} from './FilterDropdown'
import {FindScopeModal} from './FindScopeModal'
import {CountryConfigurationsButton} from './CountryConfigurationsButton'
import {ScopeLevelIcon} from './ScopeLevelIcon'

export type ScopeNavigatorVariant = 'countryDefault' | 'overriddenScopes'

export interface ScopeNavigatorProps {
  variant: ScopeNavigatorVariant
  selectedScopeId?: string
  overriddenScopes?: ScopeItem[]
  onScopeSelect?: (id: string) => void
  onTemporaryScopeSelect?: (id: string) => void
  rootScopeId?: string
  temporaryScope?: ScopeItem | null
  onOpenScope?: (scope: ScopeResultItem) => void
  /** Verify a scope via API search */
  onVerify?: (level: ScopeLevel, scopeValue: string) => Promise<VerifyScopeResult>
  /** Text for the back button (e.g. "RMC · United Kingdom") */
  backLabel?: string
  /** Callback when back button is clicked */
  onBack?: () => void
  /** Entity name displayed as a heading (e.g. "Retarder") */
  entityTitle?: string
  /** Short description shown in an info tooltip next to the entity title */
  entityTooltip?: string
}

export const ScopeNavigator = ({
  variant,
  selectedScopeId,
  overriddenScopes = [],
  onScopeSelect,
  onTemporaryScopeSelect,
  rootScopeId,
  temporaryScope = null,
  onOpenScope,
  onVerify,
  backLabel,
  onBack,
  entityTitle,
  entityTooltip
}: ScopeNavigatorProps) => {
  const {t} = useTranslation()
  const [search, setSearch] = useState('')
  const [levelFilter, setLevelFilter] = useState<ScopeLevelFilterValue>(SCOPE_LEVEL_FILTER_ALL)
  const [addModalOpen, setAddModalOpen] = useState(false)

  const levelOptions = SCOPE_LEVEL_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
    icon: opt.icon,
    iconClassName: opt.iconClassName,
  }))

  const isCountryDefaultSelected =
    variant === 'overriddenScopes' && (!selectedScopeId || selectedScopeId === rootScopeId)

  const showTemporaryScope = temporaryScope
    && variant === 'overriddenScopes'
    && !overriddenScopes.some(s => s.id === temporaryScope.id)

  const filteredScopes = useMemo(
    () =>
      overriddenScopes.filter((scope) => {
        const matchesSearch =
          scope.displayName.toLowerCase().includes(search.toLowerCase()) ||
          (scope.displayNumber?.toLowerCase().includes(search.toLowerCase()) ?? false)
        const matchesLevel = levelFilter === SCOPE_LEVEL_FILTER_ALL || scope.level === levelFilter
        return matchesSearch && matchesLevel
      }),
    [overriddenScopes, search, levelFilter]
  )

  return (
    <DashboardCard className="flex flex-col !p-4 !pb-3 gap-4">
      {(backLabel || entityTitle) && (
        <div className="flex flex-col gap-1">
          {backLabel && onBack && (
            <CountryConfigurationsButton
              onClick={onBack}
              className="flex items-center gap-1 py-2 text-xs leading-4 text-primary hover:underline cursor-pointer"
            >
              <Icon path={mdiChevronLeft} size="regular" />
              {backLabel}
            </CountryConfigurationsButton>
          )}
          {entityTitle && (
            <div className="flex items-baseline gap-1 [&>h3]:mb-0">
              <H3>{entityTitle}</H3>
              {entityTooltip && (
                <CountryConfigsTooltip content={entityTooltip} delayMs={300}>
                  <span className="self-center shrink-0 flex translate-y-0.5 cursor-default" aria-label="More information">
                    <Icon path={mdiInformationOutline} size="regular" className="text-foreground" />
                  </span>
                </CountryConfigsTooltip>
              )}
            </div>
          )}
        </div>
      )}
      {variant === 'overriddenScopes' && (
        <Button
          variant="secondary"
          type="button"
          onClick={() => setAddModalOpen(true)}
          className="w-full -mb-2"
        >
          {t('countryConfigurations.entity.scopeNavigator.findScope')}
        </Button>
      )}

      <div className="flex flex-col gap-2">
        <CountryConfigurationsButton
          icon={variant === 'countryDefault' || isCountryDefaultSelected ? mdiCheck : undefined}
          className={`flex items-center justify-between w-full min-h-[44px] px-3 py-[10px] rounded-lg border border-border cursor-pointer ${variant === 'countryDefault' || isCountryDefaultSelected ? 'bg-container_background_selected' : 'bg-container_background hover:bg-container_background_hover'}`}
          onClick={() => rootScopeId && onScopeSelect?.(rootScopeId)}
        >
          <div className="flex items-center gap-2">
            <Icon path={mdiEarth} size="regular" />
            <TextSmallRegular>
              {t('countryConfigurations.entity.scopeNavigator.countryDefault')}
            </TextSmallRegular>
          </div>
        </CountryConfigurationsButton>

        {showTemporaryScope && (
        <CountryConfigurationsButton
          icon={selectedScopeId === temporaryScope.id ? mdiCheck : undefined}
          className={`flex items-center justify-between w-full min-h-[44px] px-3 py-[10px] rounded-lg border border-border cursor-pointer ${
            selectedScopeId === temporaryScope.id
              ? 'bg-container_background_selected'
              : 'bg-container_background hover:bg-container_background_hover'
          }`}
          onClick={() => onTemporaryScopeSelect?.(temporaryScope.id)}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <ScopeLevelIcon level={temporaryScope.level} />
            <div className="flex flex-col min-w-0">
              <TextSmallRegular className="truncate">{temporaryScope.displayName}</TextSmallRegular>
              {temporaryScope.level && (
                <span className="text-xs text-foreground_faded leading-4 truncate">
                  {t(SCOPE_LEVEL_OPTIONS.find(o => o.value === temporaryScope.level)?.labelKey ?? '')}
                </span>
              )}
            </div>
          </div>
        </CountryConfigurationsButton>
      )}
      </div>

      {variant === 'countryDefault' && (
        <TextSmallRegular className="w-full text-center text-foreground_faded">
          {t('countryConfigurations.entity.scopeNavigator.countryOnlyMessage')}
        </TextSmallRegular>
      )}

      {variant === 'overriddenScopes' && (
        <>
          {overriddenScopes.length === 0 ? (
            <div className="flex flex-col items-center py-6 gap-4">
              <div className="flex flex-col items-start gap-1">
                <TextSemiBold className="self-stretch text-center">
                  {t('countryConfigurations.entity.scopeNavigator.noOverriddenScopes')}
                </TextSemiBold>
                <p className="self-stretch text-center text-foreground_faded text-base font-normal leading-6">
                  {t('countryConfigurations.entity.scopeNavigator.noOverriddenScopesHint')}
                </p>
              </div>
            </div>
          ) : (
            <>
              <TextSemiBold>
                {t('countryConfigurations.entity.scopeNavigator.scopesWithOverrides')}
              </TextSemiBold>

              {/* Level filter + Name or ID search */}
              <div className="flex flex-col gap-2">
                <FilterDropdown
                  value={levelFilter}
                  onChange={(v) => setLevelFilter(v as ScopeLevelFilterValue)}
                  options={levelOptions}
                  label={t('countryConfigurations.entity.scopeNavigator.levelAll')}
                  allValue={SCOPE_LEVEL_FILTER_ALL}
                  size="medium"
                  data-cy="scope-navigator-level-filter"
                />

                {/* Name or ID */}
                <ControlledInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  label={t('countryConfigurations.entity.scopeNavigator.nameOrId')}
                  icon={mdiMagnify}
                  variant="regular"
                  className="w-full"
                />
              </div>

              {/* Scope list */}
              {filteredScopes.length > 0 ? (
                <div className="flex flex-col w-full max-h-[288px] overflow-auto rounded-lg border border-border [scrollbar-width:thin] [scrollbar-color:#d6d3d1_#fafafa] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-neutral-50 [&::-webkit-scrollbar-track]:border-l [&::-webkit-scrollbar-track]:border-gray-200 [&::-webkit-scrollbar-track]:mt-1 [&::-webkit-scrollbar-track]:mb-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-stone-300 [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:[background-clip:padding-box]">
                  {filteredScopes.map((scope, index) => (
                    <CountryConfigurationsButton
                      key={scope.id}
                      icon={selectedScopeId === scope.id ? mdiCheck : undefined}
                      className={`relative flex items-center gap-2 justify-between w-full min-h-[44px] px-4 py-2 cursor-pointer hover:bg-container_background_hover ${selectedScopeId === scope.id ? 'bg-container_background_selected hover:bg-container_background_selected' : ''} ${index < filteredScopes.length - 1 ? 'after:absolute after:bottom-0 after:left-4 after:right-4 after:h-px after:bg-border' : ''}`}
                      onClick={() => onScopeSelect?.(scope.id)}
                    >
                      <ScopeLevelIcon level={scope.level} />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-base font-normal text-foreground leading-6 truncate w-full">{scope.displayName}</span>
                        {(scope.level || scope.displayNumber) && (
                          <span className="text-xs text-foreground_faded leading-4 truncate">
                            {[scope.level ? t(SCOPE_LEVEL_OPTIONS.find(o => o.value === scope.level)?.labelKey ?? '') : null, scope.displayNumber].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </div>
                    </CountryConfigurationsButton>
                  ))}
                </div>
              ) : (
                <div className="self-stretch inline-flex flex-col justify-center items-center">
                  <div className="self-stretch px-3 pt-10 flex flex-col justify-start items-center">
                    <img src={emptyBackground} alt="" />
                    <H3 className="self-stretch text-center text-base whitespace-nowrap leading-6 mt-3">
                      {t('countryConfigurations.entity.scopeNavigator.scopeNotFound')}
                    </H3>
                    <TextSmallRegular className="self-stretch text-center text-foreground_faded mt-1">
                      {t('countryConfigurations.entity.scopeNavigator.scopeNotFoundHint')}
                    </TextSmallRegular>
                    <div className="self-stretch flex flex-col items-center mt-4">
                      <Button
                        variant="primary"
                        type="button"
                        size="regular"
                        onClick={() => setAddModalOpen(true)}
                        iconPrefix={<Icon path={mdiMagnify} size="regular" />}
                        className="w-full"
                      >
                        {t('countryConfigurations.entity.scopeNavigator.findScope')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <FindScopeModal
            open={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            onVerify={onVerify ?? (async () => ({found: false}))}
            onOpenScope={(scope) => {
              if (onOpenScope) {
                onOpenScope(scope)
              } else {
                onScopeSelect?.(scope.id ?? '')
              }
              setAddModalOpen(false)
            }}
          />
        </>
      )}
    </DashboardCard>
  )
}
